import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Switch,
  FormControlLabel,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccountTree as LogicIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon
} from '@mui/icons-material';
import LogicRuleEditor from './LogicRuleEditor';
import API_CONFIG, { getApiUrl } from '../../config/api';

const LogicRulesModal = ({ 
  open, 
  onClose, 
  questions, 
  surveyId, 
  userId 
}) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingRule, setEditingRule] = useState(null);
  const canCreateRule = questions.length >= 2 && questions.every(question =>
    question.id && !String(question.id).startsWith('temp-')
  );

  const getSurveyScopedUrl = (endpoint, params = {}) => {
    if (!surveyId) {
      throw new Error('Survey ID is required to manage logic rules');
    }

    const query = new URLSearchParams({
      survey_id: String(surveyId),
      ...params
    });
    return `${getApiUrl(endpoint)}?${query.toString()}`;
  };

  // Fetch all logic rules when the modal opens
  useEffect(() => {
    if (open) {
      fetchAllRules();
    }
  }, [open, surveyId]);

  const fetchAllRules = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(getSurveyScopedUrl(API_CONFIG.ENDPOINTS.LOGIC_RULES.LIST));
      const data = await response.json();
      if (!response.ok || data.status !== 'success') {
        throw new Error(data.message || 'Unable to load survey logic rules');
      }
      setRules((data.data?.rules || []).map(rule => ({
        ...rule,
        enabled: rule.enabled === true || rule.enabled === 1 || rule.enabled === '1'
      })));
    } catch (err) {
      console.error('Failed to fetch rules:', err);
      setError(`Failed to load logic rules: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRule = () => {
    // Find the first question that has at least one question before it
    const targetQuestionIndex = questions.findIndex((_, index) => index > 0);
    
    if (targetQuestionIndex === -1 || !canCreateRule) {
      setError('Save all questions before creating logic rules.');
      return;
    }

    const targetQuestion = questions[targetQuestionIndex];
    
    const newRule = {
      id: null,
      isNew: true,
      name: '',
      description: '',
      enabled: true,
      priority: rules.length,
      source_question_id: questions[0].id,
      conditions: [{
        id: Date.now().toString(),
        questionId: questions[0].id,
        operator: 'equals',
        value: '',
        logicOperator: 'and'
      }],
      actions: [{
        id: Date.now().toString(),
        type: 'show_question',
        targetId: targetQuestion.id,
        value: ''
      }]
    };
    
    setEditingRule(newRule);
  };

  const handleEditRule = (rule) => {
    setEditingRule({
      ...rule,
      isNew: false
    });
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this logic rule?')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(getSurveyScopedUrl(API_CONFIG.ENDPOINTS.LOGIC_RULES.DELETE, {
        rule_id: String(ruleId)
      }), {
        method: 'DELETE',
      });

      const data = await response.json();

      if (response.ok && data.status === 'success') {
        setRules(rules.filter(rule => rule.id !== ruleId));
      } else {
        throw new Error(data.message || 'Failed to delete rule');
      }
    } catch (error) {
      console.error('Error deleting rule:', error);
      setError(`Failed to delete rule: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleRule = async (ruleId, enabled) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(getSurveyScopedUrl(API_CONFIG.ENDPOINTS.LOGIC_RULES.TOGGLE, {
        action: 'toggle',
        rule_id: String(ruleId)
      }), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          survey_id: surveyId,
          rule_id: ruleId,
          enabled
        })
      });

      const data = await response.json();

      if (response.ok && data.status === 'success') {
        setRules(currentRules => currentRules.map(rule =>
          rule.id === ruleId ? { ...rule, enabled } : rule
        ));
      } else {
        throw new Error(data.message || 'Failed to toggle rule');
      }
    } catch (error) {
      console.error('Error toggling rule:', error);
      setError(`Failed to toggle rule: ${error.message}`);
      // Revert the change in UI
    } finally {
      setLoading(false);
    }
  };

  const handleMoveRule = async (index, offset) => {
    const reordered = [...rules];
    const destination = index + offset;
    [reordered[index], reordered[destination]] = [reordered[destination], reordered[index]];
    const prioritizedRules = reordered.map((rule, priority) => ({ ...rule, priority }));
    setLoading(true);
    setError(null);
    try {
      await Promise.all(prioritizedRules.map(async rule => {
        const response = await fetch(getSurveyScopedUrl(API_CONFIG.ENDPOINTS.LOGIC_RULES.UPDATE), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            survey_id: surveyId,
            id: rule.id,
            name: rule.name,
            description: rule.description,
            enabled: rule.enabled,
            priority: rule.priority,
            conditions: rule.conditions,
            actions: rule.actions
          })
        });
        const data = await response.json();
        if (!response.ok || data.status !== 'success') {
          throw new Error(data.message || 'Unable to save rule priority');
        }
      }));
      setRules(prioritizedRules);
    } catch (err) {
      setError(`Failed to change rule priority: ${err.message}`);
      await fetchAllRules();
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRule = async (ruleData) => {
    setLoading(true);
    setError(null);
    
    try {
      const endpoint = ruleData.isNew 
        ? API_CONFIG.ENDPOINTS.LOGIC_RULES.CREATE 
        : API_CONFIG.ENDPOINTS.LOGIC_RULES.UPDATE;
      
      const method = ruleData.isNew ? 'POST' : 'PUT';
      
      const payload = {
        survey_id: surveyId,
        source_question_id: ruleData.source_question_id, // Added missing field
        name: ruleData.name,
        description: ruleData.description,
        enabled: ruleData.enabled,
        priority: ruleData.priority,
        conditions: ruleData.conditions,
        actions: ruleData.actions
      };
      
      if (!ruleData.isNew) {
        payload.id = ruleData.id;
      }

      const candidate = { ...payload, id: payload.id || `new-rule-${Date.now()}` };
      const validationRules = [
        ...rules.filter(rule => rule.id !== ruleData.id),
        candidate
      ];
      const validationResponse = await fetch(getSurveyScopedUrl(API_CONFIG.ENDPOINTS.LOGIC_RULES.VALIDATE), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ survey_id: surveyId, rules: validationRules })
      });
      const validationData = await validationResponse.json();
      if (!validationResponse.ok || validationData.status !== 'success') {
        throw new Error(validationData.message || 'Unable to validate logic rule');
      }
      if (!validationData.is_valid) {
        const validationErrors = [
          ...(validationData.validation_result?.errors || []),
          ...(validationData.validation_result?.circular_references || [])
        ];
        throw new Error(validationErrors.map(item => item.description || item.message || JSON.stringify(item)).join('; ') || 'The rule is invalid');
      }
      
      const response = await fetch(getSurveyScopedUrl(endpoint), {
        method: method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      if (response.ok && data.status === 'success') {
        // Update local state with the saved rule
        if (ruleData.isNew) {
          const newRule = {
            ...ruleData,
            id: data.data.rule_id || Date.now().toString(),
            isNew: false
          };
          setRules([...rules, newRule]);
        } else {
          setRules(rules.map(rule => 
            rule.id === ruleData.id ? { ...ruleData, isNew: false } : rule
          ));
        }
        
        setEditingRule(null);
        
        // Refresh rules list to ensure consistency
        await fetchAllRules();
      } else {
        throw new Error(data.message || 'Failed to save rule');
      }
    } catch (error) {
      console.error('Error saving rule:', error);
      setError(`Failed to save rule: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getQuestionById = (questionId) => {
    return questions.find(q => q.id === questionId);
  };

  const generateRuleSummary = (rule) => {
    const sourceQuestion = getQuestionById(rule.source_question_id);
    const questionIndex = questions.findIndex(q => q.id === rule.source_question_id);
    const sourceQuestionName = sourceQuestion 
      ? (sourceQuestion.question_text || sourceQuestion.title || `Question ${questionIndex + 1}`)
      : 'Unknown question';

    const conditionSummary = rule.condition_summary || 
      (rule.conditions?.length > 0 ? `${rule.conditions.length} condition(s)` : 'No conditions');
    
    const actionSummary = rule.action_summary || 
      (rule.actions?.length > 0 ? `${rule.actions.length} action(s)` : 'No actions');

    return `IF "${sourceQuestionName}" ${conditionSummary} THEN ${actionSummary}`;
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Logic Rules
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Logic rules determine when questions are shown or hidden based on previous answers.
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Typography variant="subtitle1">
              All Logic Rules ({rules.length})
            </Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddRule}
              variant="contained"
              size="small"
              disabled={loading || !canCreateRule}
            >
              Add Rule
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : rules.length === 0 ? (
          <Box sx={{ 
            p: 4, 
            textAlign: 'center', 
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'grey.50'
          }}>
            <LogicIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="body1" gutterBottom>
              No logic rules defined yet
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Logic rules help you create dynamic surveys that adapt to respondents' answers.
            </Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddRule}
              variant="contained"
              sx={{ mt: 2 }}
              disabled={!canCreateRule}
            >
              Create Your First Rule
            </Button>
            {!canCreateRule && (
              <Alert severity="info" sx={{ mt: 2, mx: 'auto', maxWidth: 400 }}>
                Save at least two questions before creating logic rules.
              </Alert>
            )}
          </Box>
        ) : (
          <Box>
            {rules.map((rule, index) => (
              <Card key={rule.id} sx={{ mb: 2, opacity: rule.enabled ? 1 : 0.6 }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Grid container spacing={2}>
                    <Grid item xs={1}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Tooltip title="Move up">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleMoveRule(index, -1)}
                              disabled={index === 0 || loading}
                            >
                              <ArrowUpIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Move down">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleMoveRule(index, 1)}
                              disabled={index === rules.length - 1 || loading}
                            >
                              <ArrowDownIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={8}>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="subtitle1">
                            {rule.name || `Rule ${index + 1}`}
                          </Typography>
                          
                          {!rule.enabled && (
                            <Typography variant="caption" color="text.disabled">
                              (Disabled)
                            </Typography>
                          )}
                        </Box>

                        <Typography variant="body2" color="text.secondary">
                          {generateRuleSummary(rule)}
                        </Typography>

                        {rule.description && (
                          <Typography 
                            variant="caption" 
                            color="text.disabled"
                            sx={{ display: 'block', mt: 0.5 }}
                          >
                            {rule.description}
                          </Typography>
                        )}
                      </Box>
                    </Grid>

                    <Grid item xs={3}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={rule.enabled}
                              onChange={(e) => handleToggleRule(rule.id, e.target.checked)}
                              disabled={loading}
                            />
                          }
                          label="Active"
                        />
                        
                        <Tooltip title="Edit Rule">
                          <IconButton
                            size="small"
                            onClick={() => handleEditRule(rule)}
                            disabled={loading}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Delete Rule">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteRule(rule.id)}
                            disabled={loading}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>

      {/* Rule Editor Modal */}
      {editingRule && (
        <LogicRuleEditor
          rule={editingRule}
          question={getQuestionById(editingRule.source_question_id)}
          availableQuestions={questions.filter(question => question.id && !String(question.id).startsWith('temp-'))}
          targetQuestions={questions.filter(question => question.id && !String(question.id).startsWith('temp-'))}
          onSave={handleSaveRule}
          onCancel={() => setEditingRule(null)}
          loading={loading}
        />
      )}
    </Dialog>
  );
};

export default LogicRulesModal;